import { Typography } from '@mui/material'
import {
  DataGrid,
  type DataGridProps,
  type GridColumns,
  GridOverlay,
  type GridRowParams
} from '@mui/x-data-grid'
import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { TeamIntegrationTableActions } from 'components/status-checks/common/team-integration-table/TeamIntegrationTableActions'
import { TeamIntegrationTableName } from 'components/status-checks/common/team-integration-table/TeamIntegrationTableName'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusChecksSearchContext } from 'components/status-checks/StatusChecksSearchState'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import useAuth from 'hooks/use-auth'
import {
  ObservabilityToolType,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { externalIntegrationsStatusChecksPut } from 'utils/endpoints-access'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles,
  safeRowClick
} from 'utils/mui-components/data-grid-utils'

const EmptyState: React.FC = () => {
  return (
    <GridOverlay>
      <Typography>No Authentications</Typography>
    </GridOverlay>
  )
}

export const TeamIntegrationTable: React.FC<{
  hasLogoOnIntegration?: boolean
  headerName?: string
  isAwsIntegrationsTable?: boolean
  onChange: () => void
  savedIntegrations: TeamExternalIntegration[]
}> = ({
  hasLogoOnIntegration = true,
  headerName,
  isAwsIntegrationsTable = false,
  onChange,
  savedIntegrations
}) => {
  const { hasEndpointAccess } = useAuth()

  const { evaluatedTeamId } = useStatusCheckExternalIntegrationsContext()

  const canEditAWSAccountIds = hasEndpointAccess(
    externalIntegrationsStatusChecksPut,
    {
      teamId: evaluatedTeamId
    }
  )

  const navigate = useNavigate()
  const statusChecksUrls = getStatusChecksUrls()

  // all AWS auth will have the same `name` - appending the domain to the name so it's easier
  // for the user to tell which account id (domain) they are looking at
  const savedIntegrationsWithModifiedAWSNames = useMemo(
    () =>
      savedIntegrations.map((savedIntegration) => {
        if (
          savedIntegration.observabilityToolType === ObservabilityToolType.AWS
        ) {
          return {
            ...savedIntegration,
            displayName: `${savedIntegration.name} (Account ID: ${savedIntegration.domain})`
          }
        }

        return {
          ...savedIntegration,
          displayName: savedIntegration.name
        }
      }),
    [savedIntegrations]
  )

  const searchState = useStatusChecksSearchContext()
  const { pageNum, setPageNum } = searchState.authentications

  const handleRowClick: DataGridProps['onRowClick'] = (
    params: GridRowParams<TeamExternalIntegration>,
    event
  ) => {
    if (isAwsIntegrationsTable) return

    safeRowClick(event, () => {
      navigate(
        statusChecksUrls.editAuthenticationFromTeamExternalIntegration(
          params.row
        )
      )
    })
  }

  const tableColumns: GridColumns<TeamExternalIntegration> = [
    {
      ...defaultColumnProps,
      field: 'displayName',
      headerName: headerName ?? 'Observability Tool',
      flex: 3,
      renderCell: (params) => {
        return (
          <TeamIntegrationTableName
            hasLogoOnIntegration={hasLogoOnIntegration}
            isAwsIntegrationsTable={isAwsIntegrationsTable}
            params={params}
            savedIntegrations={savedIntegrations}
          />
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => {
        return (
          <TeamIntegrationTableActions
            isAwsIntegrationsTable={isAwsIntegrationsTable}
            params={params}
            savedIntegrations={savedIntegrations}
            onChange={onChange}
          />
        )
      }
    }
  ]

  return (
    <DataGrid<TeamExternalIntegration>
      {...defaultGridProps}
      columns={tableColumns}
      columnVisibilityModel={{
        actions: isAwsIntegrationsTable ? canEditAWSAccountIds : true
      }}
      components={{
        NoRowsOverlay: EmptyState
      }}
      getRowId={(row) =>
        row.domain
          ? `${row.name}-${row.url}-${row.domain}`
          : `${row.name}-${row.url}`
      }
      page={pageNum}
      rows={savedIntegrationsWithModifiedAWSNames}
      sx={getGridStyles({
        clickableRow: !isAwsIntegrationsTable
      })}
      onPageChange={setPageNum}
      onRowClick={handleRowClick}
    />
  )
}
